import ReactMarkdown from 'react-markdown';

export default function MarkdownRenderer({ content, parameters = {}, renderMarkdown = true, className = '', fontSize = 'text-sm' }) {
  // Replace parameter placeholders with actual values
  const replaceParameters = (text) => {
    let replacedText = text;
    Object.entries(parameters).forEach(([key, value]) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      replacedText = replacedText.replace(regex, value);
    });
    return replacedText;
  };

  const processedContent = replaceParameters(content);

  // Render as raw text
  if (!renderMarkdown) {
    return (
      <div className={`${fontSize} whitespace-pre-wrap ${className}`} style={{ color: 'var(--color-text-primary)' }}>
        {processedContent}
      </div>
    );
  }

  // Render as markdown
  return (
    <div className={`max-w-none ${className}`}>
      <ReactMarkdown
        components={{
          // Custom styling for markdown elements with theme-aware colors
          h1: ({node, ...props}) => (
            <h1 className="text-2xl font-bold mb-4 mt-2" style={{ color: 'var(--color-text-primary)' }} {...props} />
          ),
          h2: ({node, ...props}) => (
            <h2 className="text-xl font-bold mb-3 mt-4" style={{ color: 'var(--color-text-primary)' }} {...props} />
          ),
          h3: ({node, ...props}) => (
            <h3 className="text-lg font-bold mb-2 mt-3" style={{ color: 'var(--color-text-primary)' }} {...props} />
          ),
          h4: ({node, ...props}) => (
            <h4 className="text-base font-semibold mb-1.5 mt-3" style={{ color: 'var(--color-text-secondary)' }} {...props} />
          ),
          p: ({node, ...props}) => (
            <p className={`mb-3 leading-relaxed ${fontSize}`} style={{ color: 'var(--color-text-primary)' }} {...props} />
          ),
          ul: ({node, ...props}) => (
            <ul className={`list-disc list-outside ml-5 mb-3 space-y-1.5 ${fontSize}`} style={{ color: 'var(--color-text-primary)' }} {...props} />
          ),
          ol: ({node, ...props}) => (
            <ol className={`list-decimal list-outside ml-5 mb-3 space-y-1.5 ${fontSize}`} style={{ color: 'var(--color-text-primary)' }} {...props} />
          ),
          li: ({node, ...props}) => (
            <li className={`ml-0 pl-1 ${fontSize}`} style={{ color: 'var(--color-text-primary)' }} {...props} />
          ),
          pre: ({node, children, ...props}) => (
            <pre className="px-4 py-3 rounded-lg text-sm font-mono overflow-x-auto mb-3 whitespace-pre" style={{backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)', borderWidth: '1px', borderColor: 'var(--color-border-default)'}} {...props}>{children}</pre>
          ),
          code: ({node, inline, className, children, ...props}) => {
            // If inline is explicitly true, it's inline code
            // If inline is undefined/false, it's block code (inside pre)
            if (inline) {
              return <code className="px-2 py-0.5 rounded-md text-sm font-mono" style={{display: 'inline', backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)', borderWidth: '1px', borderColor: 'var(--color-border-default)'}} {...props}>{children}</code>;
            }
            // Block code - just render children (pre tag handles styling)
            return <>{children}</>;
          },
          blockquote: ({node, ...props}) => (
            <blockquote className={`border-l-4 pl-4 py-2 italic mb-3 rounded-r ${fontSize}`} style={{ borderColor: 'var(--color-border-strong)', backgroundColor: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' }} {...props} />
          ),
          strong: ({node, ...props}) => (
            <strong className={`font-bold ${fontSize}`} style={{ color: 'var(--color-text-primary)' }} {...props} />
          ),
          em: ({node, ...props}) => (
            <em className={`italic ${fontSize}`} style={{ color: 'var(--color-text-secondary)' }} {...props} />
          ),
          a: ({node, ...props}) => (
            <a className="underline underline-offset-2 transition-colors" style={{ color: 'var(--color-info)', textDecorationColor: 'var(--color-info)' }} {...props} />
          ),
          hr: ({node, ...props}) => (
            <hr className="my-4 border-0 h-px" style={{ backgroundColor: 'var(--color-border-default)' }} {...props} />
          ),
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
}
